// Production refund workflow (Phase 4A).
//
// The ONLY refund entry point in the admin UI. Every action here is a call to
// the `admin-refunds` edge function — this component never writes
// class_payments, public.refunds or financial_ledger_entries directly.
//
// Flow: compose → server dry run → explicit confirmation → execute → result.

import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import RefundHistory from "./RefundHistory";
import {
  callRefundService as defaultCallRefundService,
  canExecute,
  classifyResult,
  blockerMessage,
  confirmSentence,
  failureMessage,
  formatCents,
  isFullRefund,
  MANUAL_METHODS,
  type ManualMethod,
  outcomeSentence,
  parseDollarsToCents,
  REFUND_REASONS,
  type RefundChannel,
  type RefundPreview,
  type RefundResultState,
  refundingSentence,
  remainingRefundableCents,
  resolveReason,
  type ServiceCall,
  serviceErrorMessage,
  validateAmountCents,
} from "@/lib/refunds";

export interface RefundDialogPayment {
  id: string;
  studentName: string;
  parentName: string;
  email: string;
  className: string;
  sessionLabel: string | null;
  amountCents: number;
  alreadyRefundedCents: number;
  stripeSessionId: string | null;
  /** Every student covered by this single class payment (sibling safety). */
  coveredStudents?: string[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment: RefundDialogPayment;
  /** Called after a completed refund so the caller can refresh canonical state. */
  onRefunded: () => void;
  /** Injected in tests. Defaults to the real edge-function transport. */
  callService?: typeof defaultCallRefundService;
}

type Step = "compose" | "confirm" | "result";

export function RefundDialog({ open, onOpenChange, payment, onRefunded, callService }: Props) {
  const call = callService ?? defaultCallRefundService;
  // A Stripe checkout id only makes the Stripe *option* visible. The service
  // remains authoritative about whether a card refund is actually possible,
  // and the UI never silently converts a Stripe refund into a manual one.
  const stripeOptionAvailable = Boolean(payment.stripeSessionId);

  const remaining = remainingRefundableCents(payment.amountCents, payment.alreadyRefundedCents);
  const siblings = (payment.coveredStudents ?? []).filter(Boolean);
  const multiStudent = siblings.length > 1;

  const [step, setStep] = useState<Step>("compose");
  // Null = the admin has not chosen a channel yet. Manual and Stripe are
  // distinct financial actions, so when both are legitimately available the
  // admin must pick one explicitly.
  const [channel, setChannel] = useState<RefundChannel | null>(stripeOptionAvailable ? null : "manual");
  const [amountText, setAmountText] = useState((remaining / 100).toFixed(2));
  const [reasonChoice, setReasonChoice] = useState<string>(REFUND_REASONS[2]);
  const [reasonCustom, setReasonCustom] = useState("");
  const [method, setMethod] = useState<ManualMethod>("zelle");
  const [referenceNote, setReferenceNote] = useState("");
  const [attested, setAttested] = useState(false);
  const [preview, setPreview] = useState<RefundPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ state: RefundResultState; call: ServiceCall } | null>(null);
  const [rechecking, setRechecking] = useState(false);

  // Reset everything whenever the dialog is opened for a payment.
  useEffect(() => {
    if (!open) return;
    setStep("compose");
    setChannel(stripeOptionAvailable ? null : "manual");
    setAmountText((remaining / 100).toFixed(2));
    setReasonChoice(REFUND_REASONS[2]);
    setReasonCustom("");
    setMethod("zelle");
    setReferenceNote("");
    setAttested(false);
    setPreview(null);
    setPreviewError(null);
    setSubmitting(false);
    setResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payment.id]);

  const amountCents = parseDollarsToCents(amountText);
  const reason = resolveReason(reasonChoice, reasonCustom);
  const amountCheck = validateAmountCents(amountCents, remaining);
  const full = amountCents !== null && isFullRefund(amountCents, remaining);
  const methodLabel = useMemo(
    () => MANUAL_METHODS.find((m) => m.value === method)?.label ?? "manual transfer",
    [method],
  );

  // Any change to the request invalidates the dry run — execution stays locked
  // until a fresh preview matches exactly what will be sent. Attestation and
  // the reference note are part of the request, so they invalidate it too.
  useEffect(() => {
    setPreview(null);
    setPreviewError(null);
    setStep("compose");
  }, [amountText, channel, reasonChoice, reasonCustom, method, referenceNote, attested]);

  const gate = channel
    ? canExecute({ preview, amountCents, channel, reason, attested, submitting })
    : { allowed: false, blocker: "Choose whether this is a Stripe card refund or a manual record." };


  const runDryRun = useCallback(async () => {
    if (!channel || !amountCheck.ok || !reason.trim() || amountCents === null) return;

    setPreviewing(true);
    setPreviewError(null);
    try {
      const res = await call({
        action: "dry_run",
        class_payment_id: payment.id,
        amount_cents: amountCents,
        reason,
        channel,
      });
      if (res.status >= 400) {
        setPreview(null);
        setPreviewError(serviceErrorMessage(res));
        return;
      }
      setPreview(res.body as unknown as RefundPreview);
    } finally {
      setPreviewing(false);
    }
  }, [amountCheck.ok, amountCents, call, channel, payment.id, reason]);

  const execute = useCallback(async () => {
    if (!gate.allowed || amountCents === null) return;
    setSubmitting(true);
    try {
      const res = await call(
        channel === "stripe"
          ? { action: "stripe", class_payment_id: payment.id, amount_cents: amountCents, reason }
          : {
            action: "manual",
            class_payment_id: payment.id,
            amount_cents: amountCents,
            reason,
            method,
            attestation_confirmed: true,
            reference_note: referenceNote.trim() || undefined,
          },
      );
      const state = classifyResult(res);
      setResult({ state, call: res });
      setStep("result");
      if (state === "completed") onRefunded();
    } finally {
      setSubmitting(false);
    }
  }, [amountCents, call, channel, gate.allowed, method, onRefunded, payment.id, reason, referenceNote]);

  const resultRefundId = (result?.call.body?.refund as { id?: string } | undefined)?.id ?? null;

  const recheck = useCallback(async () => {
    if (!resultRefundId) return;
    setRechecking(true);
    try {
      const res = await call({ action: "reconcile", refund_id: resultRefundId });
      const state = classifyResult(res);
      setResult({ state, call: res });
      if (state === "completed") onRefunded();
    } finally {
      setRechecking(false);
    }
  }, [call, onRefunded, resultRefundId]);

  const sessionLine = payment.sessionLabel
    ? `${payment.className} · ${payment.sessionLabel}`
    : payment.className;

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Refund — {payment.studentName}</DialogTitle>
          <DialogDescription>
            {payment.parentName} · {sessionLine}
          </DialogDescription>
        </DialogHeader>

        {step === "compose" && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted/30 p-3 text-xs">
              <Field label="Original payment" value={formatCents(payment.amountCents)} />
              <Field label="Already refunded" value={formatCents(payment.alreadyRefundedCents)} />
              <Field label="Remaining refundable" value={formatCents(remaining)} />
              <Field label="Channel" value={channel === "stripe" ? "Stripe" : channel === "manual" ? "Manual" : "Not chosen"} />
            </div>

            {multiStudent && (
              <p className="rounded-md border border-amber-300 bg-amber-50/60 p-2 text-[11px]" data-testid="multi-student-notice">
                This single payment covers {siblings.length} students: {siblings.join(", ")}. Any refund
                applies to the whole payment, not just {payment.studentName}.
              </p>
            )}

            {remaining <= 0 ? (
              <p className="text-xs text-muted-foreground">
                This payment has already been refunded in full. No further refund is possible.
              </p>
            ) : (
              <>
                {/* Channel — explicit radio choice; Stripe and manual are
                    different financial actions and are never auto-swapped. */}
                <fieldset className="space-y-1">
                  <legend className="text-xs font-medium">Refund channel</legend>
                  {stripeOptionAvailable && (
                    <label className="flex items-start gap-2 text-xs">
                      <input
                        type="radio"
                        name="refund-channel"
                        value="stripe"
                        className="mt-0.5"
                        checked={channel === "stripe"}
                        onChange={() => setChannel("stripe")}
                      />
                      <span>Stripe card refund — sends money back to the card</span>
                    </label>
                  )}
                  <label className="flex items-start gap-2 text-xs">
                    <input
                      type="radio"
                      name="refund-channel"
                      value="manual"
                      className="mt-0.5"
                      checked={channel === "manual"}
                      onChange={() => setChannel("manual")}
                    />
                    <span>Manual record — money already returned outside Stripe</span>
                  </label>
                  {!stripeOptionAvailable && (
                    <p className="text-[11px] text-muted-foreground">
                      This payment has no Stripe checkout on file, so only a manual refund record is available.
                      Only record one if the money really was returned another way.
                    </p>
                  )}
                </fieldset>



                {/* Amount */}
                <div className="space-y-1">
                  <Label className="text-xs" htmlFor="refund-amount">
                    Refund amount (USD)
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="refund-amount"
                      inputMode="decimal"
                      className="h-8 w-32 text-xs"
                      value={amountText}
                      onChange={(e) => setAmountText(e.target.value)}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setAmountText((remaining / 100).toFixed(2))}
                    >
                      Full refund ({formatCents(remaining)})
                    </Button>
                  </div>
                  {!amountCheck.ok && amountText.trim() !== "" && (
                    <p className="text-[11px] text-destructive" role="alert">
                      {amountCheck.error}
                    </p>
                  )}
                  {amountCheck.ok && amountCents !== null && (
                    <div className="text-[11px] text-muted-foreground">
                      <div>{refundingSentence(amountCents, remaining)}</div>
                      <div className={full ? "" : "text-foreground"}>{outcomeSentence(amountCents, remaining)}</div>
                    </div>
                  )}
                </div>

                {/* Reason */}
                <div className="space-y-1">
                  <Label className="text-xs">Reason</Label>
                  <Select value={reasonChoice} onValueChange={setReasonChoice}>
                    <SelectTrigger className="h-8 text-xs" aria-label="Refund reason">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REFUND_REASONS.map((r) => (
                        <SelectItem key={r} value={r}>
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {reasonChoice === "Other" && (
                    <Textarea
                      aria-label="Custom refund reason"
                      className="text-xs"
                      rows={2}
                      value={reasonCustom}
                      onChange={(e) => setReasonCustom(e.target.value)}
                      placeholder="Describe the reason for this refund"
                    />
                  )}
                </div>

                {/* Manual-only fields */}
                {channel === "manual" && (
                  <div className="space-y-2 rounded-md border border-amber-400 bg-amber-50/60 p-3">
                    <p className="text-[11px] font-medium" data-testid="manual-warning">
                      A manual refund does not send any money. It only records money you have already
                      returned to the family outside this system.
                    </p>

                    <div className="space-y-1">
                      <Label className="text-xs">Method</Label>
                      <Select value={method} onValueChange={(v) => setMethod(v as ManualMethod)}>
                        <SelectTrigger className="h-8 text-xs" aria-label="Manual refund method">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MANUAL_METHODS.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs" htmlFor="refund-reference">
                        Reference / note (optional)
                      </Label>
                      <Input
                        id="refund-reference"
                        className="h-8 text-xs"
                        value={referenceNote}
                        onChange={(e) => setReferenceNote(e.target.value)}
                      />
                    </div>
                    <label className="flex items-start gap-2 text-xs">
                      <Checkbox
                        checked={attested}
                        onCheckedChange={(v) => setAttested(v === true)}
                        aria-label="I confirm these funds have actually been sent or returned to the family."
                      />
                      <span>I confirm these funds have actually been sent or returned to the family.</span>
                    </label>
                  </div>
                )}

                {/* Dry run */}
                <div className="space-y-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={!channel || !amountCheck.ok || !reason.trim() || previewing}
                    onClick={runDryRun}
                  >
                    {previewing && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                    Run refund preview
                  </Button>
                  {previewError && (
                    <p className="text-[11px] text-destructive" role="alert">
                      {previewError}
                    </p>
                  )}
                  {preview && <PreviewCard preview={preview} methodLabel={methodLabel} remaining={remaining} />}
                </div>
              </>
            )}

            <div className="space-y-1 border-t pt-3">
              <div className="text-xs font-medium">Refund history</div>
              <RefundHistory paymentId={payment.id} />
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={!gate.allowed}
                title={gate.blocker ?? undefined}
                onClick={() => setStep("confirm")}
              >
                Continue
              </Button>
            </DialogFooter>
            {!gate.allowed && gate.blocker && (
              <p className="text-[11px] text-muted-foreground" data-testid="execution-blocker">
                {gate.blocker}
              </p>
            )}
          </div>
        )}

        {step === "confirm" && amountCents !== null && channel !== null && (
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
              <div className="text-3xl font-bold tracking-tight" data-testid="confirm-amount">
                {formatCents(amountCents)}
              </div>
              <div className="mt-1 text-xs font-medium">
                {payment.studentName} · {payment.parentName}
              </div>
              <div className="text-xs text-muted-foreground">{payment.email}</div>
              <div className="text-xs text-muted-foreground">{sessionLine}</div>
              <div className="mt-2 space-y-0.5 text-xs">
                <Field label="Original payment" value={formatCents(payment.amountCents)} />
                <Field label="Already refunded" value={formatCents(payment.alreadyRefundedCents)} />
                <Field label="Refunding now" value={formatCents(amountCents)} />
                <Field label="Remaining paid after" value={formatCents(remaining - amountCents)} />
              </div>
              <div className="mt-2 text-xs font-medium">
                {channel === "stripe" ? "Stripe card refund" : `Manual record · ${methodLabel}`}
              </div>
              {multiStudent && (
                <div className="mt-1 text-[11px]">
                  Covers {siblings.length} students on one payment: {siblings.join(", ")}.
                </div>
              )}
            </div>
            <p
              className={`flex items-start gap-2 rounded-md p-2 text-xs ${
                channel === "stripe"
                  ? "border border-destructive/40 bg-destructive/5"
                  : "border border-amber-400 bg-amber-50/70"
              }`}
              data-testid="confirm-warning"
            >
              <AlertTriangle
                className={`mt-0.5 h-4 w-4 shrink-0 ${channel === "stripe" ? "text-destructive" : "text-amber-600"}`}
              />
              <span>{confirmSentence(channel, amountCents, methodLabel)}</span>
            </p>
            <p className="text-xs text-muted-foreground">{outcomeSentence(amountCents, remaining)}</p>
            <DialogFooter className="flex-col gap-2 sm:flex-row">
              <Button variant="outline" size="sm" disabled={submitting} onClick={() => setStep("compose")}>
                Back
              </Button>

              <Button
                size="sm"
                variant="destructive"
                disabled={submitting || !gate.allowed}
                onClick={execute}
              >
                {submitting && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                {submitting
                  ? "Processing…"
                  : channel === "stripe"
                    ? `Refund ${formatCents(amountCents)}`
                    : `Record ${formatCents(amountCents)} already returned`}

              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "result" && result && (
          <div className="space-y-3 text-sm">
            {result.state === "completed" && (
              <div className="flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50/60 p-3">
                <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
                <div className="text-xs">
                  <div className="font-medium">
                    Refund complete — {formatCents(Number(result.call.body?.refund && (result.call.body.refund as { amount_cents?: number }).amount_cents) || (amountCents ?? 0))}
                  </div>
                  <div className="text-muted-foreground">
                    {channel === "stripe" ? "Sent back through Stripe." : `Recorded as returned by ${methodLabel}.`}
                  </div>
                </div>
              </div>
            )}

            {result.state === "processing" && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50/60 p-3">
                <Clock className="mt-0.5 h-4 w-4 text-amber-600" />
                <div className="text-xs">
                  <div className="font-medium">Stripe has not yet confirmed the final refund status.</div>
                  <div>Do not submit another refund.</div>
                </div>
              </div>
            )}

            {result.state === "failed" && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/5 p-3">
                <XCircle className="mt-0.5 h-4 w-4 text-destructive" />
                <div className="text-xs">
                  <div className="font-medium">Refund failed</div>
                  <div>{failureMessage()}</div>

                  <div className="text-muted-foreground">The payment was not changed.</div>
                </div>
              </div>
            )}

            {result.state === "rejected" && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/5 p-3">
                <XCircle className="mt-0.5 h-4 w-4 text-destructive" />
                <div className="text-xs">
                  <div className="font-medium">Refund not accepted</div>
                  <div>{serviceErrorMessage(result.call)}</div>
                  <div className="text-muted-foreground">No refund was recorded.</div>
                </div>
              </div>
            )}

            <div className="space-y-1 border-t pt-3">
              <div className="text-xs font-medium">Refund history</div>
              <RefundHistory paymentId={payment.id} />
            </div>

            <DialogFooter className="gap-2">
              {result.state === "processing" && resultRefundId && (
                <Button variant="outline" size="sm" disabled={rechecking} onClick={recheck}>
                  {rechecking && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                  Recheck status
                </Button>
              )}
              <Button size="sm" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PreviewCard({
  preview,
  methodLabel,
  remaining,
}: {
  preview: RefundPreview;
  methodLabel: string;
  remaining: number;
}) {
  return (
    <div className="space-y-1 rounded-md border bg-background/60 p-3 text-xs" data-testid="refund-preview">
      <div className="font-medium">Preview (nothing has been charged yet)</div>
      <Field label="Family" value={`${preview.family.adult_name} · ${preview.family.email}`} />
      <Field label="Student" value={preview.student ?? "—"} />
      <Field label="Session" value={preview.class.session_label ?? "—"} />
      <Field label="Original payment" value={formatCents(preview.original_amount_cents)} />
      <Field label="Already refunded" value={formatCents(preview.already_completed_refund_cents)} />
      <Field
        label="Remaining refundable"
        value={formatCents(
          preview.original_amount_cents - preview.already_completed_refund_cents - preview.open_refund_cents,
        )}
      />
      <Field label="Requested refund" value={formatCents(preview.requested_refund_cents)} />
      <Field label="Remaining paid after" value={formatCents(preview.remaining_balance_after_cents)} />
      <Field label="Channel" value={preview.channel === "stripe" ? "Stripe" : `Manual · ${methodLabel}`} />
      <Field label="Financial period" value={preview.financial_period_open ? "Open" : "Closed"} />
      {preview.dispute_present && (
        <p className="text-destructive">A dispute is present on this charge.</p>
      )}
      <p>{refundingSentence(preview.requested_refund_cents, remaining)}</p>
      <p>{outcomeSentence(preview.requested_refund_cents, remaining)}</p>
      {!preview.would_execute && (
        <p className="font-medium text-destructive" role="alert" data-testid="preview-blocker">
          {blockerMessage(preview.blocker, preview.blocker_message)}
        </p>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

export default RefundDialog;
